import { ApiService } from "../api/ApiService";

export interface ContextSourceConfig {
  base_url?: string;
  capabilities_path?: string;
  auth_header_name?: string;
  server_url?: string;
  connection_string?: string;
  query_template?: string;
  table_schema?: string;
  access_type?: "public" | "private";
  credential_type?: "basic_auth" | "bearer_token" | "api_key_header" | "oauth2";
  custom_headers?: string;
}

export interface ContextSourcePayload {
  source_type: "api" | "mcp" | "text_document";
  config: ContextSourceConfig;
  credential_value: string;
}

export interface ContextSourceResponse {
  id: number;
  source_type: string;
  config: ContextSourceConfig;
  capabilities: any;
  last_capabilities_fetch: string;
  is_active: boolean;
  credential_id?: string | null;
  user_id?: string;
  agents?: Array<{ agent_id?: string }>;
}

class ContextSourceService {
  async get(sourceId: number): Promise<ContextSourceResponse | null> {
    const response = await ApiService.invoke({
      resource: "context-sources",
      action: "get",
      id: sourceId.toString(),
    });

    return response ?? null;
  }

  async getForAgent(agentId: string): Promise<ContextSourceResponse | null> {
    const response = await ApiService.invoke({
      resource: "context-sources",
      action: "list",
      data: { agentId } as any,
    });

    if (Array.isArray(response) && response.length > 0) {
      return response[0];
    }

    if (response && Array.isArray((response as { data?: ContextSourceResponse[] }).data)) {
      const data = (response as { data: ContextSourceResponse[] }).data;
      return data.length > 0 ? data[0] : null;
    }

    return null;
  }

  async listUserSources(): Promise<ContextSourceResponse[]> {
    const response = await ApiService.invoke({
      resource: "context-sources",
      action: "list",
      data: {} as any,
    });

    if (Array.isArray(response)) {
      return response;
    }

    if (response && Array.isArray((response as { data?: ContextSourceResponse[] }).data)) {
      return (response as { data: ContextSourceResponse[] }).data;
    }

    return [];
  }

  async create(payload: ContextSourcePayload): Promise<ContextSourceResponse> {
    const response = await ApiService.invoke({
      resource: "context-sources",
      action: "create",
      data: payload as any,
    });

    if (!response) {
      throw new Error("Failed to create integration");
    }

    return response;
  }

  async attachToAgent(agentId: string, sourceId: number): Promise<ContextSourceResponse> {
    const response = await ApiService.invoke({
      resource: "context-sources",
      action: "attach",
      data: { agentId, sourceId } as any,
    });

    if (!response) {
      throw new Error("Failed to attach integration to agent");
    }

    return response;
  }

  async upsert(agentId: string, sourceId: number, payload: ContextSourcePayload): Promise<ContextSourceResponse> {
    const response = await ApiService.invoke({
      resource: "context-sources",
      action: "upsert",
      data: { agentId, sourceId, ...payload } as any,
    });

    if (!response) {
      throw new Error("Failed to upsert context source");
    }

    return response;
  }

  async remove(agentId: string, sourceId?: number): Promise<void> {
    await ApiService.invoke({
      resource: "context-sources",
      action: "delete",
      data: { agentId, ...(sourceId && { sourceId }) } as any,
    });
  }

  async removeSource(sourceId: string): Promise<void> {
    await ApiService.invoke({
      resource: "context-sources",
      action: "delete-source",
      data: { sourceId } as any,
    });
  }

  async refreshCapabilities(agentId: string): Promise<any> {
    const response = await ApiService.invoke({
      resource: "context-sources",
      action: "refresh-capabilities",
      data: { agentId } as any,
    });

    return response;
  }

  async toggleActive(sourceId: number, isActive: boolean): Promise<void> {
    await ApiService.invoke({
      resource: "context-sources",
      action: "update",
      id: sourceId.toString(),
      data: { is_active: isActive } as any,
    });
  }
}

export const contextSourceService = new ContextSourceService();
